// Minutes and billing (ORG-04, BILL-01..06): this month's AI minutes, the plan, minute packs and invoices.
import { useState } from "react";
import { fmtDate, fmtDay, fmtNumber, fmtRupees } from "../lib/format";
import type { Invoice, InvoiceLine } from "../lib/types";
import { useMember } from "../app/session";
import { Bars, Card, Empty, ErrorNote, Loading, Modal, Page, Stat, useAction, useLoad } from "../app/ui";

const statusBadge: Record<string, string> = { paid: "ok", issued: "warm", void: "" };
const statusLabel: Record<string, string> = { paid: "Paid", issued: "To pay", void: "Cancelled" };

export function Usage() {
  const { api, member, tz } = useMember();
  const usage = useLoad(() => api.usage(), [api]);
  const billing = useLoad(() => api.billing(), [api]);
  const { busy, run } = useAction();
  const [open, setOpen] = useState<Invoice | null>(null);

  if ((usage.loading && !usage.data) || (billing.loading && !billing.data)) return <Loading />;
  if (!usage.data) return <ErrorNote error={usage.error} />;
  const u = usage.data;
  const b = billing.data;
  const included = u.included + (b?.summary.minutes.topped_up ?? 0);
  const left = Math.max(0, included - u.used);
  const share = included ? Math.min(100, (u.used / included) * 100) : 100;
  const today = new Date().toISOString().slice(0, 10);
  const inTrial = b?.summary.trial_ends_on != null && b.summary.trial_ends_on >= today;

  const buy = async (code: string) => {
    if (await run(() => api.buyMinutes(code), "Ordered. The minutes arrive when the invoice is paid.")) {
      await billing.reload();
    }
  };

  return (
    <Page
      title="Minutes and billing"
      sub={`Since ${fmtDay(u.month_start)}. Calls under 10 seconds are free; others count whole minutes.`}
    >
      <div className="stack">
        <div className="grid cols-3">
          <Stat
            label="Used this month"
            value={`${fmtNumber(u.used)} min`}
            hint={`${fmtNumber(u.recordings)} calls analysed`}
          />
          <Stat
            label="Left"
            value={`${fmtNumber(left)} min`}
            hint={`of ${fmtNumber(included)} this month`}
            alert={left === 0}
          />
          <Stat
            label="Waiting"
            value={fmtNumber(u.held)}
            hint="Recordings held until minutes are available"
            alert={u.held > 0}
          />
        </div>
        <Card title="This month">
          <div className={`meter${share > 90 ? " high" : ""}`}>
            <div style={{ width: `${share}%` }} />
          </div>
          <p className="faint" style={{ marginTop: 8 }}>
            When the minutes run out, calls still sync and rule-based follow-ups still appear; recordings wait until
            next month or until you add minutes below.
          </p>
          <Bars data={u.by_day.map((d) => ({ label: fmtDay(d.day), value: d.minutes }))} />
        </Card>

        <ErrorNote error={billing.error} />
        {b && (
          <div className="grid cols-2">
            <Card title={`${b.summary.plan.name} plan`}>
              <p>
                <strong>{fmtRupees(b.summary.plan.monthly_paise)}</strong> a month for {b.summary.plan.included_users}
                {" "}
                users and {fmtNumber(b.summary.minutes.included)} AI minutes; each extra user{" "}
                {fmtRupees(b.summary.plan.extra_user_paise)} a month. GST {Math.round(b.summary.gst_rate * 100)}% extra.
              </p>
              <p className="muted">
                {b.summary.users} active users. {b.summary.exempt
                  ? "This account is not billed."
                  : inTrial
                  ? `Free until ${
                    fmtDate(`${b.summary.trial_ends_on}T12:00:00Z`, tz)
                  }; after that you are invoiced at the end of each month.`
                  : "You are invoiced at the end of each month for that month."}
              </p>
            </Card>
            <Card title="Add minutes">
              <p className="faint">Minutes you add count for the month they are paid in.</p>
              <div className="stack">
                {b.summary.packs.map((p) => (
                  <div className="row between" key={p.code}>
                    <span>
                      <strong>{fmtNumber(p.minutes)} minutes</strong>{" "}
                      <span className="muted">{fmtRupees(p.price_paise)} + GST</span>
                    </span>
                    <button className="small" disabled={busy} onClick={() => void buy(p.code)}>
                      Order
                    </button>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {b && (
          <Card title="Invoices" flush>
            {b.invoices.length === 0 && <Empty>No invoices yet.</Empty>}
            {b.invoices.length > 0 && (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Invoice</th>
                      <th>For</th>
                      <th className="num">Total</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {b.invoices.map((i) => (
                      <tr key={i.id}>
                        <td>
                          <div className="primary">{i.number}</div>
                          <div className="faint">{fmtDate(i.issued_at, tz)}</div>
                        </td>
                        <td>
                          {i.kind === "subscription" && i.period_start
                            ? `Plan, ${monthName(i.period_start)}`
                            : "Minute pack"}
                        </td>
                        <td className="num nowrap">{fmtRupees(i.total_paise)}</td>
                        <td>
                          <span className={`badge ${statusBadge[i.status] ?? ""}`}>
                            {statusLabel[i.status] ?? i.status}
                          </span>
                          {i.status === "issued" && <div className="faint">Due {fmtDate(i.due_at, tz)}</div>}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <button className="small" onClick={() => setOpen(i)}>View</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="faint" style={{ padding: "0 16px 14px" }}>
              Pay by bank transfer or UPI to the account on the invoice and quote the invoice number; it is marked paid
              when the payment arrives.
            </p>
          </Card>
        )}
      </div>
      {open && b && (
        <InvoiceView
          invoice={open}
          seller={b.summary.seller}
          buyer={member.org_name}
          tz={tz}
          onClose={() => setOpen(null)}
        />
      )}
    </Page>
  );
}

function monthName(day: string): string {
  return new Date(`${day}T12:00:00Z`).toLocaleDateString("en-IN", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function InvoiceView(props: {
  invoice: Invoice;
  seller: Record<string, string>;
  buyer: string;
  tz: string;
  onClose: () => void;
}) {
  const i = props.invoice;
  const lines = (i.lines as unknown as InvoiceLine[]) ?? [];
  const right = { textAlign: "right" as const };
  return (
    <Modal title={`Invoice ${i.number}`} onClose={props.onClose}>
      <div className="row between">
        <div>
          <strong>{props.seller.name ?? "Voxowa"}</strong>
          {props.seller.address && <div className="faint">{props.seller.address}</div>}
          {props.seller.gstin && <div className="faint">GSTIN {props.seller.gstin}</div>}
        </div>
        <div style={right}>
          <div>
            Billed to <strong>{props.buyer}</strong>
          </div>
          <div className="faint">Issued {fmtDate(i.issued_at, props.tz)}</div>
        </div>
      </div>
      <table style={{ marginTop: 12 }}>
        <tbody>
          {lines.map((l, n) => (
            <tr key={n}>
              <td>
                {l.description}
                {l.quantity > 1 ? ` × ${l.quantity}` : ""}
              </td>
              <td className="num" style={right}>{fmtRupees(l.amount_paise)}</td>
            </tr>
          ))}
          <tr>
            <td className="muted">Subtotal</td>
            <td className="num" style={right}>{fmtRupees(i.subtotal_paise)}</td>
          </tr>
          <tr>
            <td className="muted">GST</td>
            <td className="num" style={right}>{fmtRupees(i.gst_paise)}</td>
          </tr>
          <tr>
            <td>
              <strong>Total</strong>
            </td>
            <td className="num" style={right}>
              <strong>{fmtRupees(i.total_paise)}</strong>
            </td>
          </tr>
        </tbody>
      </table>
      {i.status === "issued" && (props.seller.bank || props.seller.upi) && (
        <p style={{ marginTop: 10 }}>
          Pay to: {[props.seller.bank, props.seller.upi && `UPI ${props.seller.upi}`].filter(Boolean).join(" · ")}
        </p>
      )}
      <p className="faint" style={{ marginTop: 10 }}>
        {i.status === "paid"
          ? `Paid ${i.paid_at ? fmtDate(i.paid_at, props.tz) : ""}${
            i.payment_ref ? `, reference ${i.payment_ref}` : ""
          }.`
          : i.status === "void"
          ? "Cancelled."
          : `Due ${fmtDate(i.due_at, props.tz)}.`}
      </p>
      <div className="actions">
        <button onClick={() => window.print()}>Print</button>
        <button className="primary" onClick={props.onClose}>Close</button>
      </div>
    </Modal>
  );
}
